import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

const ITEM_SELECTOR = '.menu-list__list-item';
const TOGGLE_SELECTOR = '[data-submenu-toggle]';
const SUBMENU_SELECTOR = '[ref="submenu[]"]';
const LINK_SELECTOR = '[ref="menuitem"]';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A custom element that manages a header menu.
 *
 * Accessibility model (W3C APG "Disclosure Navigation Menu", WCAG 2.2):
 * every top-level entry that has children renders a link AND a sibling
 * `[data-submenu-toggle]` button. The link navigates (Enter follows it); the button
 * is the only control that opens the submenu, and it alone carries `aria-expanded` /
 * `aria-controls` so a screen reader announces a real, operable disclosure. The link
 * mirrors the open state as `data-submenu-open` purely so CSS can style it — that
 * attribute has no accessibility meaning.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The list item whose submenu is currently shown.
 * @property {boolean} pinned - Whether the submenu was opened deliberately (click / keyboard)
 *   rather than by hover. A pinned submenu ignores pointer-exit and closes only on Escape,
 *   another item opening, focus leaving, or a click outside.
 * @property {HTMLElement | null} trigger - The control that pinned it, for focus return.
 * @property {HTMLElement | null} pendingTrigger - Trigger awaiting the debounced activation.
 * @property {boolean} pendingFocus - Whether that activation should end by moving focus
 *   into the submenu (ArrowDown).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement} [overflowToggle] - The "More" button that discloses the overflow panel.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), { signal });

    // Keyboard + pointer support (WCAG 2.2): focus alone must NOT open a submenu, and
    // neither must Enter on the top-level link — that navigates. The dedicated arrow
    // button is the disclosure: Enter/Space toggles it, ArrowDown opens and moves into
    // the submenu, Escape closes it and returns focus to the button.
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    // Safari/Firefox don't focus a <button> on click, so focusout can't be relied on to
    // retract a pinned submenu — watch for clicks landing outside the menu instead.
    document.addEventListener('click', this.#handleDocumentClick, { signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    pinned: false,
    trigger: null,
    pendingTrigger: null,
    pendingFocus: false,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Open a menu item's submenu on purpose (click / keyboard) rather than by hover, so it
   * stays put until it is explicitly dismissed.
   * @param {HTMLElement} listItem - The list item that owns the trigger.
   * @param {HTMLElement} trigger - The control the user operated.
   */
  #open(listItem, trigger) {
    this.#state.pendingTrigger = trigger;
    this.activate(/** @type {any} */ ({ target: listItem }));
  }

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    // Claim the pending intent up front — this handler is debounced, so a stale one
    // must never leak into a later hover-driven activation.
    const pendingTrigger = this.#state.pendingTrigger;
    const pendingFocus = this.#state.pendingFocus;
    this.#state.pendingTrigger = null;
    this.#state.pendingFocus = false;

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      setExpanded(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.#state.pinned = Boolean(pendingTrigger);
    this.#state.trigger = pendingTrigger;
    this.ariaExpanded = 'true';
    setExpanded(item, true);
    this.#syncOverflowToggle();

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenuHeight > 0) {
      const headerEl = this.closest('#header-component');
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        document.documentElement.style.setProperty('--nav-overlay-top', `${Math.round(rect.bottom)}px`);
      }
    }

    document.documentElement.toggleAttribute('data-nav-open', submenuHeight > 0);

    // Position caret to point at the hovered nav item for wide custom submenus
    const isCustomSubmenu =
      submenu &&
      (submenu.classList.contains('menu-list__submenu--promo-cards') ||
        submenu.classList.contains('menu-list__submenu--image-hover'));
    if (isCustomSubmenu) {
      const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
      const trigger = item.querySelector('.menu-list__trigger') ?? item;
      if (submenuInner) {
        const itemRect = trigger.getBoundingClientRect();
        const innerRect = submenuInner.getBoundingClientRect();
        const rawLeft = itemRect.left + itemRect.width / 2 - innerRect.left;
        const caretLeft = Math.max(20, Math.min(rawLeft, innerRect.width - 20));
        submenuInner.style.setProperty('--caret-left', `${caretLeft}px`);
      }
    }

    // Focus last: reading offsetHeight above already flushed layout, so the submenu is
    // genuinely visible by now and can take focus. (A queued rAF would be a race — the
    // debounced activation is not guaranteed to run before the next frame.)
    if (pendingFocus) focusIntoSubmenu(item, event.target !== item);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    // A submenu the user opened on purpose must not vanish when the pointer drifts off.
    if (this.#state.pinned) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * The arrow button (and the overflow "More" button) is the only control that opens a
   * submenu — Enter/Space on a <button> fires click natively, so this one handler covers
   * mouse, touch and keyboard activation alike. The sibling link is untouched and keeps
   * navigating.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const toggle = target.closest(TOGGLE_SELECTOR);
    if (!(toggle instanceof HTMLElement)) return;

    const listItem = toggle.closest(ITEM_SELECTOR);
    if (!(listItem instanceof HTMLElement)) return;

    const item = findMenuItem(listItem);
    if (!item) return;

    // Safari/Firefox skip focusing a clicked button; do it explicitly so Escape has
    // somewhere to return focus to and Tab continues from the right place.
    toggle.focus();

    if (this.#state.activeItem === item) {
      if (this.#state.pinned) {
        this.#debouncedActivateHandler.cancel();
        this.#deactivate(item);
        return;
      }

      // Already showing because the pointer is over it — the click pins it open so the
      // first press never reads as a no-op (or, on touch, as open-then-immediately-close).
      this.#state.pinned = true;
      this.#state.trigger = toggle;
      return;
    }

    this.#open(listItem, toggle);
  };

  /**
   * Keyboard control for the top-level menu. Enter/Space on the disclosure button is
   * handled natively (it dispatches click); this adds the arrow-key and Escape behavior
   * of the W3C APG disclosure navigation pattern.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    // Escape closes the open submenu and returns focus to the control that opened it.
    if (event.key === 'Escape') {
      const active = this.#state.activeItem;
      if (!active) return;

      const restoreTo = this.#state.trigger ?? findToggle(active);
      const focusWasInside = this.contains(document.activeElement);

      this.#debouncedActivateHandler.cancel();
      this.#deactivate(active);

      if (focusWasInside && restoreTo?.isConnected) restoreTo.focus();
      event.stopPropagation();
      return;
    }

    const toggle = target.closest(TOGGLE_SELECTOR);
    if (!(toggle instanceof HTMLElement)) return;

    const listItem = toggle.closest(ITEM_SELECTOR);
    if (!(listItem instanceof HTMLElement)) return;

    const item = findMenuItem(listItem);
    if (!item) return;

    if (event.key === 'ArrowDown') {
      // Open (never close) and move into the submenu, so the first child link is one
      // keystroke away rather than a Tab past the trigger.
      event.preventDefault();

      if (this.#state.activeItem === item) {
        this.#state.pinned = true;
        this.#state.trigger = toggle;
        focusIntoSubmenu(item, listItem !== item);
      } else {
        // Already visible submenus can take focus now; a new one only once the
        // debounced activation has run (which is where the focus move happens).
        this.#state.pendingFocus = true;
        this.#open(listItem, toggle);
      }
      return;
    }

    if (event.key === 'ArrowUp' && this.#state.activeItem === item) {
      event.preventDefault();
      this.#debouncedActivateHandler.cancel();
      this.#deactivate(item);
    }
  };

  /**
   * Retract the active submenu once focus moves outside of its menu item entirely.
   * Keyboard focus moving between the trigger and its own submenu items keeps it open.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const active = this.#state.activeItem;
    if (!active) return;

    const next = event.relatedTarget;
    const trigger = this.#state.trigger;

    // Focus is still within this item's trigger/submenu — leave it open. The trigger is
    // checked separately because the overflow "More" button sits outside the item whose
    // submenu it reveals.
    if (next instanceof Node && (active.contains(next) || trigger?.contains(next))) return;

    // Don't fight an active mouse hover on the overflow menu.
    if (this.overflowHovered) return;

    this.#debouncedActivateHandler.cancel();
    this.#deactivate(active);
  };

  /**
   * Dismiss a pinned submenu when the user clicks anywhere outside the menu.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    const active = this.#state.activeItem;
    if (!active || !this.#state.pinned) return;

    const { target } = event;
    if (target instanceof Node && this.contains(target)) return;

    this.#debouncedActivateHandler.cancel();
    this.#deactivate(active);
  };

  /**
   * Keep the overflow "More" button's announced state in step with the panel it reveals.
   */
  #syncOverflowToggle() {
    const toggle = this.refs.overflowToggle;
    if (!(toggle instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', this.dataset.overflowExpanded === 'true' ? 'true' : 'false');
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    document.documentElement.removeAttribute('data-nav-open');

    this.#state.activeItem = null;
    this.#state.pinned = false;
    this.#state.trigger = null;
    this.ariaExpanded = 'false';
    setExpanded(item, false);
    this.#syncOverflowToggle();

    const link = item.querySelector(LINK_SELECTOR);
    if (!(link instanceof HTMLElement)) return;

    link.setAttribute('data-animating', '');

    setTimeout(() => {
      link.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu item (the <li>) whose submenu an event refers to.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const item = element.closest(ITEM_SELECTOR);

  if (item?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    const first = item.parentElement?.querySelector('[slot="overflow"]');
    return first instanceof HTMLElement ? first : null;
  }

  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the submenu belonging to a menu item.
 * @param {Element | null | undefined} item
 * @returns {HTMLElement | null}
 */
function findSubmenu(item) {
  const submenu = item?.querySelector(SUBMENU_SELECTOR);
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the disclosure button belonging to a menu item.
 * @param {Element | null | undefined} item
 * @returns {HTMLElement | null}
 */
function findToggle(item) {
  const toggle = item?.querySelector(TOGGLE_SELECTOR);
  return toggle instanceof HTMLElement ? toggle : null;
}

/**
 * Reflect a menu item's open state: `aria-expanded` on the disclosure button (the part
 * assistive tech reads) and a styling-only `data-submenu-open` on the link.
 * @param {HTMLElement} item
 * @param {boolean} expanded
 */
function setExpanded(item, expanded) {
  findToggle(item)?.setAttribute('aria-expanded', String(expanded));

  const link = item.querySelector(LINK_SELECTOR);
  if (link instanceof HTMLElement) link.toggleAttribute('data-submenu-open', expanded);
}

/**
 * Move focus to the first thing inside a newly opened disclosure.
 * @param {HTMLElement} item - The menu item that owns the submenu.
 * @param {boolean} fromOverflowToggle - True when opened from the "More" button, whose
 *   panel starts with the overflowing items themselves rather than a submenu.
 */
function focusIntoSubmenu(item, fromOverflowToggle) {
  const first = fromOverflowToggle
    ? item.querySelector(LINK_SELECTOR)
    : findSubmenu(item)?.querySelector(FOCUSABLE_SELECTOR);

  if (first instanceof HTMLElement) first.focus();
}
